import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button3D } from '@/components/ui/button-3d';
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from '@/components/ui/carousel';

import bg1 from '@/components/images/background-onboarding/01-background-onboarding.jpg';
import bg2 from '@/components/images/background-onboarding/02-background-onboarding.jpg';
import bg3 from '@/components/images/background-onboarding/03-background-onboarding.jpg';
import onboardingLogo from '@/components/images/logo/logo-orcamigo-onboarding.png';

const slides = [
  {
    bg: bg1,
    title: 'Organize seus clientes',
    description: 'Tenha todos os contatos e histórico na palma da sua mão.',
  },
  {
    bg: bg2,
    title: 'Crie orçamentos profissionais',
    description: 'Gere propostas incríveis em segundos e impressione seus clientes.',
  },
  {
    bg: bg3,
    title: 'Controle seu negócio',
    description: 'Saiba exatamente quanto você faturou e o que está pendente.',
  },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  // Auto-advance slides
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 4500);

    return () => clearInterval(interval);
  }, [api]);

  // Track current slide snaps
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const handleLogin = () => {
    navigate('/auth');
  };

  const handleGuestMode = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Don't render if user is authenticated (will redirect)
  if (user) {
    return null;
  }

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col justify-between select-none">
      {/* 1. Dynamic Background Image (z-0) */}
      <div 
        className="absolute inset-0 transition-all duration-1000 bg-cover bg-center ease-in-out z-0" 
        style={{ backgroundImage: `url(${slides[current].bg})` }}
      />

      {/* 2. Dark Overlay Gradient (z-10) */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none" 
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(15, 15, 15, 0.4) 40%, rgba(15, 15, 15, 0.95) 100%)'
        }}
      />

      {/* 3. Content and Action Slider (z-20) */}
      <div className="relative z-20 flex-1 flex flex-col justify-end px-6 pb-safe pt-20">
        {/* App Logo positioned directly above slide titles */}
        <div className="flex justify-center mb-6">
          <img 
            src={onboardingLogo}
            alt="Orçamigo"
            className="h-8 w-auto object-contain bg-transparent"
          />
        </div>

        {/* Slide text details */}
        <Carousel
          setApi={setApi}
          opts={{
            align: 'center',
            loop: true,
          }}
          className="w-full max-w-sm mx-auto"
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                <div className="flex flex-col items-center text-center px-4 pb-2">
                  {/* Title */}
                  <h2 className="mb-4 text-2xl sm:text-3xl font-bold text-white tracking-tight leading-snug">
                    {slide.title}
                  </h2>
                  
                  {/* Description */}
                  <p className="text-sm sm:text-base text-white/80 leading-relaxed max-w-[280px] sm:max-w-[320px]">
                    {slide.description}
                  </p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Carousel Pagination Dots */}
        <div className="flex justify-center gap-2 mt-6 mb-8">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                current === index
                  ? 'w-6 bg-primary'
                  : 'w-2 bg-white/40'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Buttons (Footer with Margins & Safe Areas) */}
        <div className="w-full max-w-sm mx-auto space-y-3 pb-8">
          <Button3D onClick={handleLogin} className="w-full text-base">
            Entrar ou criar conta
          </Button3D>

          <Button3D color="gray" onClick={handleGuestMode} className="w-full text-base">
            Testar sem criar conta
          </Button3D>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
